import { LiveData, Service } from '@toeverything/infra';

import type { DocService } from '../../doc';
import { type PresentationMachineState, StateMachineHelpers } from '../types';
import type { FullscreenService } from './fullscreen.service';
import type { PresentationStateMachine } from './presentation-state-machine';
import type {
  ModerationInfo,
  ModerationRecoveryState,
  SessionState,
  WorkspaceRealtimeService,
} from './workspace-realtime.service';

export type ModerationViewStatus =
  | 'offline'
  | 'resuming'
  | 'expired'
  | 'idle'
  | 'joinable'
  | 'moderating-free'
  | 'moderating-frames'
  | 'following-free'
  | 'following-frames'
  | 'following-frames-awaiting-fullscreen'
  | 'following-frames-joined';

export type ModerationParticipantStatus = 'active' | 'reconnecting';

export interface ModerationViewParticipant {
  id: string;
  userId: string;
  status: ModerationParticipantStatus;
}

export interface ModerationViewState {
  docId: string;
  status: ModerationViewStatus;
  recoveryState: ModerationRecoveryState;
  isConnected: boolean;
  activeModeration: ModerationInfo | null;
  sessionState: SessionState | null;
  stateMachineState: PresentationMachineState;
  moderatorIdentitySource: Pick<
    ModerationInfo,
    'moderatorName' | 'moderatorUserId'
  > | null;
  participants: ModerationViewParticipant[];
  shouldShowParticipantOverlay: boolean;
  shouldShowFollowInvitation: boolean;
  shouldShowFollowingStatus: boolean;
  shouldShowPresentationJoin: boolean;
  shouldShowIdlePrompt: boolean;
}

const createIdleViewState = (
  docId: string,
  recoveryState: ModerationRecoveryState,
  isConnected: boolean,
  stateMachineState: PresentationMachineState
): ModerationViewState => ({
  docId,
  status: isConnected ? 'idle' : 'offline',
  recoveryState,
  isConnected,
  activeModeration: null,
  sessionState: null,
  stateMachineState,
  moderatorIdentitySource: null,
  participants: [],
  shouldShowParticipantOverlay: false,
  shouldShowFollowInvitation: false,
  shouldShowFollowingStatus: false,
  shouldShowPresentationJoin: false,
  shouldShowIdlePrompt: false,
});

function sessionToModeration(session: SessionState): ModerationInfo {
  return {
    sessionId: session.sessionId,
    docId: session.docId,
    moderatorClientId: session.moderatorClientId,
    moderatorUserId: session.moderatorUserId,
    moderatorName: session.moderatorName,
    mode: session.mode,
    isFullscreen: session.isFullscreen,
    frameIndex: session.frameIndex,
    frameId: session.frameId,
    settings: session.settings,
    followerUserIds: [],
    reconnectingFollowerUserIds: [],
    followerCount: 0,
  };
}

function getParticipants(
  moderation: ModerationInfo | null
): ModerationViewParticipant[] {
  if (!moderation) return [];

  const activeUserIds = [...new Set(moderation.followerUserIds ?? [])];
  const activeSet = new Set(activeUserIds);
  const reconnectingUserIds = [
    ...new Set(moderation.reconnectingFollowerUserIds ?? []),
  ].filter(userId => !activeSet.has(userId));

  return [
    ...activeUserIds.map((userId, index) => ({
      id: `${userId}:active:${index}`,
      userId,
      status: 'active' as const,
    })),
    ...reconnectingUserIds.map((userId, index) => ({
      id: `${userId}:reconnecting:${index}`,
      userId,
      status: 'reconnecting' as const,
    })),
  ];
}

function getViewStatus({
  isConnected,
  recoveryState,
  activeModeration,
  stateMachineState,
  isFullscreen,
  presenterIsFullscreen,
}: {
  isConnected: boolean;
  recoveryState: ModerationRecoveryState;
  activeModeration: ModerationInfo | null;
  stateMachineState: PresentationMachineState;
  isFullscreen: boolean;
  presenterIsFullscreen: boolean;
}): ModerationViewStatus {
  if (!isConnected) return 'offline';
  if (recoveryState === 'resuming') return 'resuming';
  if (recoveryState === 'expired' && !activeModeration) return 'expired';

  if (stateMachineState.type === 'moderating_frames') {
    return 'moderating-frames';
  }
  if (stateMachineState.type === 'moderating_free') {
    return 'moderating-free';
  }
  if (stateMachineState.type === 'following_frames') {
    if (stateMachineState.isJoined || isFullscreen) {
      return 'following-frames-joined';
    }
    return presenterIsFullscreen
      ? 'following-frames-awaiting-fullscreen'
      : 'following-frames';
  }
  if (stateMachineState.type === 'following_free') {
    return 'following-free';
  }

  return activeModeration ? 'joinable' : 'idle';
}

export class ModerationViewStateService extends Service {
  constructor(
    private readonly docService: DocService,
    private readonly realtimeService: WorkspaceRealtimeService,
    private readonly stateMachine: PresentationStateMachine,
    private readonly fullscreenService: FullscreenService
  ) {
    super();
  }

  readonly viewState$ = LiveData.computed(get => {
    const docId = this.docService.doc.id;
    const isConnected = get(this.realtimeService.isConnected$);
    const recoveryState = get(this.realtimeService.moderationRecoveryState$);
    const activeModerations = get(this.realtimeService.activeModerations$);
    const sessionState = get(this.realtimeService.sessionState$);
    const stateMachineState = get(this.stateMachine.state$);
    const isFullscreen = get(this.fullscreenService.isFullscreen$);

    const docSessionState = sessionState?.docId === docId ? sessionState : null;
    const activeModeration = isConnected
      ? (activeModerations.find(moderation => moderation.docId === docId) ??
        (docSessionState ? sessionToModeration(docSessionState) : null))
      : null;
    const presenterIsFullscreen =
      docSessionState?.isFullscreen ?? activeModeration?.isFullscreen ?? false;

    const status = getViewStatus({
      isConnected,
      recoveryState,
      activeModeration,
      stateMachineState,
      isFullscreen,
      presenterIsFullscreen,
    });

    if (!isConnected || status === 'resuming' || status === 'expired') {
      return {
        ...createIdleViewState(
          docId,
          recoveryState,
          isConnected,
          stateMachineState
        ),
        status,
        sessionState: docSessionState,
      };
    }

    const participants = getParticipants(activeModeration);
    const isFollowing = StateMachineHelpers.isFollowing(stateMachineState);
    const isModerating = StateMachineHelpers.isModerating(stateMachineState);

    return {
      docId,
      status,
      recoveryState,
      isConnected,
      activeModeration,
      sessionState: docSessionState,
      stateMachineState,
      moderatorIdentitySource: activeModeration ?? docSessionState,
      participants,
      shouldShowParticipantOverlay: Boolean(activeModeration),
      shouldShowFollowInvitation: status === 'joinable',
      shouldShowFollowingStatus: isFollowing,
      shouldShowPresentationJoin:
        status === 'following-frames-awaiting-fullscreen' && !isModerating,
      shouldShowIdlePrompt: isModerating,
    };
  });
}
